import { Card, CardContent, toast } from '@sitewise/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { updateUnit } from '../../../api/buildings.js';
import {
  clearUnitCustomFieldValue,
  setUnitCustomFieldValue,
  type UnitDetail,
  type UnitFieldValue,
} from '../../../api/units.js';
import { InlineEdit, InlineYesNo } from '../../../components/inline-edit.js';
import { ApiError } from '../../../lib/api-client.js';

import { CustomFieldRenderer } from './custom-field-renderer.js';

interface Props {
  detail: UnitDetail;
}

/**
 * The Overview tab is the canonical "facts about this unit" surface.
 * Everything is editable inline — heavier multi-step edits are
 * funneled through the EditUnitSheet (opened from the page header).
 *
 * Layout: three cards stacked. (1) Identity + utilities, (2) Appliance
 * summary, (3) Custom fields. The right rail is taken up by the
 * building card and the lead-status callout.
 */
export function OverviewTab({ detail }: Props) {
  const queryClient = useQueryClient();
  const { unit, customFieldValues } = detail;

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['unit', unit.id] });
    void queryClient.invalidateQueries({
      queryKey: ['building', unit.buildingId, 'units'],
    });
  }

  const patch = useMutation({
    mutationFn: (input: Record<string, unknown>) => updateUnit(unit.id, input),
    onSuccess: () => invalidate(),
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update unit');
    },
  });

  /** InlineEdit / InlineYesNo expect a `Promise<void>` from save
   *  callbacks; the mutation resolves with the unit row, so we await
   *  + discard. */
  async function patchUnit(input: Record<string, unknown>): Promise<void> {
    await patch.mutateAsync(input);
  }

  async function setCustomField(field: UnitFieldValue, value: unknown) {
    try {
      await setUnitCustomFieldValue(unit.id, field.key, value);
      invalidate();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save value');
      throw err;
    }
  }

  async function clearCustomField(field: UnitFieldValue) {
    try {
      await clearUnitCustomFieldValue(unit.id, field.key);
      invalidate();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not clear value');
      throw err;
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardContent className="space-y-3 p-5">
            <SectionTitle>Identity & utilities</SectionTitle>
            <DefinitionGrid>
              <DefinitionRow label="Floor">
                <InlineEdit
                  label="Floor"
                  value={unit.floor}
                  onSave={(v) => patchUnit({ floor: v === '' ? null : v })}
                />
              </DefinitionRow>
              <DefinitionRow label="Bedrooms">
                <InlineEdit
                  label="Bedrooms"
                  inputType="number"
                  value={unit.bedroomCount}
                  onSave={(v) =>
                    patchUnit({
                      bedroomCount: v === '' ? null : Number(v),
                    })
                  }
                />
              </DefinitionRow>
              <DefinitionRow label="Bathrooms">
                <InlineEdit
                  label="Bathrooms"
                  inputType="number"
                  value={unit.bathroomCount}
                  onSave={(v) =>
                    patchUnit({
                      bathroomCount: v === '' ? null : Number(v),
                    })
                  }
                />
              </DefinitionRow>
              <DefinitionRow label="Square feet">
                <InlineEdit
                  label="Square feet"
                  inputType="number"
                  value={unit.squareFeet}
                  onSave={(v) =>
                    patchUnit({
                      squareFeet: v === '' ? null : Number(v),
                    })
                  }
                />
              </DefinitionRow>
              <DefinitionRow label="Gas">
                <InlineYesNo
                  label="Gas service"
                  value={unit.gasService}
                  onChange={(next) => patchUnit({ gasService: next })}
                />
              </DefinitionRow>
              <DefinitionRow label="Electric">
                <InlineYesNo
                  label="Electric service"
                  value={unit.electricService}
                  onChange={(next) => patchUnit({ electricService: next })}
                />
              </DefinitionRow>
              <DefinitionRow label="Heat source">
                <span className="text-sm capitalize text-fg-secondary">
                  {unit.heatSource ? unit.heatSource.replace(/_/g, ' ') : '—'}
                </span>
              </DefinitionRow>
              <DefinitionRow label="Hot water">
                <span className="text-sm capitalize text-fg-secondary">
                  {unit.hotWaterSource ? unit.hotWaterSource.replace(/_/g, ' ') : '—'}
                </span>
              </DefinitionRow>
              <DefinitionRow label="Central AC">
                <InlineYesNo
                  label="Central AC"
                  value={unit.centralAc}
                  onChange={(next) => patchUnit({ centralAc: next })}
                />
              </DefinitionRow>
              <DefinitionRow label="Window AC">
                <InlineEdit
                  label="Window AC count"
                  inputType="number"
                  value={unit.windowAcCount}
                  onSave={(v) =>
                    patchUnit({
                      windowAcCount: v === '' ? null : Number(v),
                    })
                  }
                />
              </DefinitionRow>
            </DefinitionGrid>
            {unit.notes ? (
              <p className="border-t border-border pt-3 text-sm text-fg-secondary">
                <span className="text-xs uppercase tracking-wide text-fg-muted">Notes</span>
                <br />
                {unit.notes}
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-5">
            <SectionTitle>Appliances</SectionTitle>
            <DefinitionGrid>
              <DefinitionRow label="Stove fuel">
                <span className="text-sm capitalize text-fg-secondary">
                  {unit.stoveFuel ? unit.stoveFuel.replace(/_/g, ' ') : '—'}
                </span>
              </DefinitionRow>
              <DefinitionRow label="Stove replaced">
                <span className="text-sm text-fg-secondary">
                  {formatDate(unit.stoveLastReplaced)}
                </span>
              </DefinitionRow>
              <DefinitionRow label="Refrigerator replaced">
                <span className="text-sm text-fg-secondary">
                  {formatDate(unit.refrigeratorLastReplaced)}
                </span>
              </DefinitionRow>
              <DefinitionRow label="Dishwasher">
                <InlineYesNo
                  label="Dishwasher present"
                  value={unit.dishwasherPresent}
                  onChange={(next) => patchUnit({ dishwasherPresent: next })}
                />
              </DefinitionRow>
              <DefinitionRow label="Dishwasher replaced">
                <span className="text-sm text-fg-secondary">
                  {formatDate(unit.dishwasherLastReplaced)}
                </span>
              </DefinitionRow>
              <DefinitionRow label="Washer/dryer">
                <InlineYesNo
                  label="Washer/dryer present"
                  value={unit.washerDryerPresent}
                  onChange={(next) => patchUnit({ washerDryerPresent: next })}
                />
              </DefinitionRow>
              <DefinitionRow label="Washer/dryer replaced">
                <span className="text-sm text-fg-secondary">
                  {formatDate(unit.washerDryerLastReplaced)}
                </span>
              </DefinitionRow>
            </DefinitionGrid>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-1 p-5">
            <SectionTitle>Custom fields</SectionTitle>
            {customFieldValues.length === 0 ? (
              <p className="text-sm text-fg-secondary">
                No custom fields defined yet.{' '}
                <CustomFieldsEmptyHint />
              </p>
            ) : (
              <div className="-mx-1.5 divide-y divide-border">
                {customFieldValues.map((field) => (
                  <CustomFieldRenderer
                    key={field.definitionId}
                    field={field}
                    onSave={(v) => setCustomField(field, v)}
                    onClear={() => clearCustomField(field)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <aside className="space-y-4">
        <Card>
          <CardContent className="space-y-2 p-5 text-sm">
            <SectionTitle>Building</SectionTitle>
            <p className="font-medium text-fg">{detail.building.name}</p>
            <p className="text-fg-secondary">
              {detail.building.addressLine1}
              {detail.building.addressLine2 ? `, ${detail.building.addressLine2}` : ''}
              <br />
              {detail.building.city}, {detail.building.state} {detail.building.postalCode}
            </p>
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}

function CustomFieldsEmptyHint() {
  const navigate = useNavigate();
  return (
    <button
      className="text-primary hover:underline"
      type="button"
      onClick={() => navigate('/settings/unit-fields')}
    >
      Define custom fields →
    </button>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{children}</h3>
  );
}

function DefinitionGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-x-4 gap-y-2">{children}</div>;
}

function DefinitionRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] items-center gap-2">
      <span className="text-xs uppercase tracking-wide text-fg-muted">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(`${value}T00:00:00Z`).toLocaleDateString();
}
